import includes from 'lodash/includes';
import { useMemo } from 'react';
import {
  PermissionSet,
  PermissionSetType,
  ProjectId,
  UserType,
} from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { UserPermission } from 'api/permissions';
import { useAccountDataQuery, useAllCompanies } from 'queries/account';
import { AccountPermissionSetType } from 'stores/permission-sets';
import { useUser } from '../use-user';

const useUserPermissions = (): UserPermission => {
  const { permissions } = useUser();

  return permissions;
};

export const useUserPermissionSetType = (): PermissionSetType => {
  const {
    permissionSet: { permissionSetType },
  } = useUserPermissions();

  return permissionSetType;
};

const useCurrentUserProjectPermission = ([, projectId]: ProjectId) => {
  const { projects } = useUserPermissions();

  return projects[projectId];
};

const useCurrentUserProjectPermissionSet = (
  projectId: ProjectId,
): PermissionSet | null => {
  const projectPermission = useCurrentUserProjectPermission(projectId);

  return projectPermission ? projectPermission.permissionSet : null;
};

const useCurrentUserProjectPermissionSetType = (
  projectId: ProjectId,
): PermissionSetType | null => {
  const permissionSet = useCurrentUserProjectPermissionSet(projectId);

  return permissionSet ? permissionSet.permissionSetType : null;
};

const useIsCurrentUserProjectAdmin = (projectId: ProjectId): boolean => {
  const type = useCurrentUserProjectPermissionSetType(projectId);
  const currentUserPermissionSetType = useUserPermissionSetType();

  return (
    type === PermissionSetType.ProjectOwner ||
    includes(
      [PermissionSetType.AccountAdmin, PermissionSetType.SuperUser],
      currentUserPermissionSetType,
    )
  );
};

const useIsCurrentUserVendorCollaborator = (projectId: ProjectId): boolean => {
  const type = useCurrentUserProjectPermissionSetType(projectId);

  return PermissionSetType.Collaborator === type;
};

const useIsCurrentUserCustomerCollaborator = (
  projectId: ProjectId,
): boolean => {
  const type = useCurrentUserProjectPermissionSetType(projectId);

  return includes(
    [
      PermissionSetType.CustomerChampion,
      PermissionSetType.CustomerCollaborator,
    ],
    type,
  );
};

interface UseCurrentUserProjectAccessResult {
  permissionSetType: PermissionSetType | null;
  isProjectAdmin: boolean;
  isVendorCollaborator: boolean;
  isCustomerCollaborator: boolean;
}

export const useCurrentUserProjectAccess = (
  projectId: ProjectId,
): UseCurrentUserProjectAccessResult => {
  const permissionSetType = useCurrentUserProjectPermissionSetType(projectId);
  const isProjectAdmin = useIsCurrentUserProjectAdmin(projectId);
  const isVendorCollaborator = useIsCurrentUserVendorCollaborator(projectId);
  const isCustomerCollaborator =
    useIsCurrentUserCustomerCollaborator(projectId);

  return {
    permissionSetType,
    isProjectAdmin,
    isVendorCollaborator,
    isCustomerCollaborator,
  };
};

export const useIsCurrentUserCollaborator = () => {
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isCollaborator =
    (currentUserPermissionSetType as any as AccountPermissionSetType) ===
    AccountPermissionSetType.VendorCollaborator;

  return isCollaborator;
};

export const useCurrentUserAcceLevel = ():
  | PartnerPermissionName
  | undefined => {
  const { data } = useAccountDataQuery();
  const userType = data?.userProfile.user.userType;
  const currentUserCompanyId = data?.userProfile.user.company?.companyId;
  const { data: partnerCompanies } = useAllCompanies();

  return useMemo(
    () =>
      userType === UserType.Partner
        ? (partnerCompanies ?? []).find(
            ({ companyId }) => companyId === currentUserCompanyId,
          )?.accessLevel?.partnerPermissionSetName
        : undefined,
    [currentUserCompanyId, partnerCompanies, userType],
  );
};
